import { AfterViewInit, Component, OnInit, QueryList, ViewChildren } from '@angular/core'
import { Depot, ProductMove, User } from 'depoto-core/src/entities'
import { CoreService } from '../../../services'
import { PopoverDirective } from 'ngx-bootstrap/popover'
import { SchemaUtil } from '../../../utils'
import { ProductDetailService } from '../../../services/product-detail.service'
import { CompanyUtilService } from '../../../services/company-util.service'

@Component({
  selector: 'product-moves-list',
  templateUrl: 'product-moves-list.component.html',
  styleUrls: ['product-moves-list.component.scss'],
})
export class ProductMovesListComponent implements AfterViewInit, OnInit {
  @ViewChildren(PopoverDirective) popovers: QueryList<PopoverDirective>
  depots: Depot[] = []
  productMoves: ProductMove[] = []
  operationTypes: string[] = ['all', 'in', 'out']
  selectedOperationType = 'all'
  selectedDepotFrom = 0
  selectedDepotTo = 0
  filterFulltext: string
  filterFrom: string
  filterTo: string
  filterUser = 0
  users: User[] = []
  batches: string[] = ['all']
  selectedBatch = 'all'
  expirationDates: string[] = ['all']
  selectedExpirationDate = 'all'
  totalItems = 0
  currentPage: number
  endPage: number
  sortProp = 'created'
  sortDir = 'desc'
  loading = true
  scrollDebounceTimeout = null
  intersectionObserver = null

  constructor(
    private core: CoreService,
    private productDetailService: ProductDetailService,
    public companyUtil: CompanyUtilService,
  ) {}

  async ngOnInit() {
    this.loadViewSettings()
    await this.refreshFiltersData()
    await this.filter(1)
  }

  loadViewSettings(): void {
    const settings = this.core.services.storage.getSync(this.core.services.storage.keys.views.productMovesList)
    if (settings && Object.keys(settings).length > 0) {
      this.selectedOperationType = settings.selectedOperationType || 'all'
      this.selectedDepotFrom = settings.selectedDepotFrom
      this.selectedDepotTo = settings.selectedDepotTo
      this.sortProp = settings.sortProp
      this.sortDir = settings.sortDir
    }
  }

  saveViewSettings(): void {
    const settings = {
      selectedOperationType: this.selectedOperationType,
      selectedDepotFrom: this.selectedDepotFrom,
      selectedDepotTo: this.selectedDepotTo,
      sortProp: this.sortProp,
      sortDir: this.sortDir,
    }
    this.core.services.storage.set(this.core.services.storage.keys.views.productMovesList, settings)
  }

  get queryFilters() {
    const f: any = {
      product: this.productDetailService.product.id,
      depotFrom: this.selectedDepotFrom > 0 ? this.selectedDepotFrom : undefined,
      depotTo: this.selectedDepotTo > 0 ? this.selectedDepotTo : undefined,
      operationType: this.selectedOperationType !== 'all' ? this.selectedOperationType : undefined,
      batch: this.selectedBatch !== 'all' ? this.selectedBatch : undefined,
      expirationDate: this.selectedExpirationDate !== 'all' ? this.selectedExpirationDate : undefined,
      user: this.filterUser > 0 ? this.filterUser : undefined,
    }
    Object.keys(f).forEach(k => {
      if (f[k] === undefined) {
        delete f[k]
      }
    })
    return f
  }

  async clearFilters(type?: string) {
    if (type !== null && type === 'depots') {
      this.selectedDepotFrom = 0
      this.selectedDepotTo = 0
    } else {
      this.selectedOperationType = 'all'
      this.selectedDepotFrom = 0
      this.selectedDepotTo = 0
      this.selectedBatch = 'all'
      this.selectedExpirationDate = 'all'
      this.filterUser = 0
    }
    this.saveViewSettings()
  }

  async refreshFiltersData(): Promise<any> {
    this.depots = [...this.productDetailService.depots]
    if (this.depots.filter(d => d.id === 0).length === 0) {
      this.depots.unshift({ id: 0, name: 'all', unavailablesUrl: '', checkouts: [], users: [] })
    }
    this.users = [
      { id: 0, email: 'all' },
      ...(await this.core.services.user
        .getList({}, { id: null, firstName: null, lastName: null, name: null, email: null })
        .then(result => result.items)),
    ]
    this.loadFiltersFromStock()
  }

  loadFiltersFromStock() {
    const batches = []
    const expirationDates = []
    if (this.productDetailService.stock && this.productDetailService.stock.length > 0) {
      this.productDetailService.stock.forEach(s => {
        s.items.forEach(si => {
          if (si.batch) {
            batches.push(si.batch)
          }
          if (si.expirationDate) {
            expirationDates.push(si.expirationDate)
          }
        })
      })
    }
    this.batches = ['all', ...batches]
    this.expirationDates = ['all', ...Array.from(new Set(expirationDates))]
  }

  async switchDepots() {
    const depotFrom: number = this.selectedDepotFrom
    this.selectedDepotFrom = this.selectedDepotTo
    this.selectedDepotTo = depotFrom
    this.saveViewSettings()
    await this.filter(1)
  }

  async filter(pageNo = 1, sortProp: string = this.sortProp, sortDir: string = this.sortDir, concat = false) {
    this.loading = concat ? false : true
    this.currentPage = pageNo

    setTimeout(async () => {
      const options: any = {
        product: this.productDetailService.product.id,
      }
      if (this.selectedOperationType !== 'all') {
        options.type = this.selectedOperationType
      }
      if (this.selectedDepotFrom > 0) {
        options.depotFrom = Number(this.selectedDepotFrom)
      }
      if (this.selectedDepotTo > 0) {
        options.depotTo = Number(this.selectedDepotTo)
      }
      if (this.filterUser > 0) {
        options.createdBy = Number(this.filterUser)
      }
      if (this.selectedBatch && this.selectedBatch.length > 0 && this.selectedBatch !== 'all') {
        options.batch = this.selectedBatch
      }
      if (
        this.selectedExpirationDate &&
        this.selectedExpirationDate.length > 0 &&
        this.selectedExpirationDate !== 'all'
      ) {
        options.expirationDate = this.selectedExpirationDate
      }
      if (this.filterFrom || this.filterTo) {
        options.created = {}
        if (this.filterFrom) {
          options.created.left_date = this.filterFrom
        }
        if (this.filterTo) {
          options.created.right_date = this.filterTo
        }
      }
      const result = await this.core.services.productMove.getList(
        { page: pageNo, sort: sortProp, direction: sortDir, filters: options },
        SchemaUtil.productMove,
      )
      if (concat) {
        const itemsWithFilteredUsersDepots =
          result?.items?.filter(item =>
            this.depots.some(depot => {
              return depot.id === item.productDepotFrom?.depot?.id || depot.id === item.productDepotTo?.depot?.id
            }),
          ) || []
        itemsWithFilteredUsersDepots.forEach(move => {
          this.productMoves.push(new ProductMove(move))
        })
      } else {
        const itemsWithFilteredUsersDepots =
          result?.items?.filter(item =>
            this.depots.some(depot => {
              return depot.id === item.productDepotFrom?.depot?.id || depot.id === item.productDepotTo?.depot?.id
            }),
          ) || []
        this.productMoves = itemsWithFilteredUsersDepots.map(move => new ProductMove(move))
      }
      this.totalItems = result?.paginator ? result.paginator.totalCount : 0
      this.endPage = result?.paginator ? result.paginator.endPage : 0
      this.saveViewSettings()
      this.productDetailService.productMoves = this.productMoves
      setTimeout(() => {
        this.loading = false
        this.initIntersectionObserver()
      }, 300)
    }, 300)
  }

  async sortBy(property: string) {
    if (this.sortProp === property) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortProp = property
    }
    this.currentPage = 1
    await this.filter()
    this.saveViewSettings()
  }

  initIntersectionObserver() {
    const el = document.getElementById('next-page-btn')
    if (el) {
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(el)
      }
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !this.loading && !this.scrollDebounceTimeout) {
            this.filter(this.currentPage, this.sortProp, this.sortDir, true)
            this.scrollDebounceTimeout = setTimeout(() => {
              this.scrollDebounceTimeout = null
            }, 700)
          }
        },
        { threshold: [0] },
      )
      this.intersectionObserver.observe(el)
    }
  }
  ngAfterViewInit() {
    this.popovers.forEach((popover: PopoverDirective) => {
      popover.onShown.subscribe(() => {
        this.popovers.filter(p => p !== popover).forEach(p => p.hide())
      })
    })
  }

  get product() {
    return this.productDetailService.product
  }
}
